import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ThemeProvider } from 'styled-components'
import NextNprogress from 'nextjs-progressbar'
import Alert from 'react-s-alert'
import ClipLoader from 'react-spinners/ClipLoader'
import { theme, GlobalStyles } from 'theme'
import {
  LIGHT_THEME,
  COLOR_THEME_OPTIONS,
  LOCAL_STORAGE_KEY_THEME,
  LOCAL_STORAGE_LANGUAGE,
  DARK_THEME,
  SYSTEM_THEME,
  LOCAL_STORAGE_KEY_DIRECTION,
  DIRECTION_RIGHT_TO_LEFT,
  LANGUAGE_ENGLISH,
  SUPPORTED_LANGUAGES,
} from 'consts'
import withoutPageDataProps from 'utils/withoutPageDataProps'
import errorHelper from 'utils/errorHelper'
import { Flex } from 'components/atoms/Layout'
import AlertTemplate from 'shared/AlertTemplate'
import '@material-ui/styles'
import '@reach/dialog/styles.css'
import 'sanitize.css'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-s-alert/dist/s-alert-default.css'
import 'react-quill/dist/quill.snow.css'
import 'fonts.css'
import 'slick-carousel/slick/slick.css'
import 'tui-image-editor/dist/tui-image-editor.css'
import 'tui-color-picker/dist/tui-color-picker.css'
import 'intro.js/introjs.css'
import { IntlProvider, FormattedRelative, useIntl, injectIntl } from 'react-intl'
import moment, { lang } from 'moment'
import ErrorBoundary from './ErrorBoundary'
import Head from 'next/head'

const App = ({ user, children, title, path }) => {
  // let messages_to_use =
  // let locale = LANGUAGE_ENGLISH

  // if (typeof navigator !== 'undefined' && navigator && navigator.language && navigator.language.includes('ru-RU')) {
  //   locale = 'ru'
  //   require(`moment/locale/ru`)
  //   moment.locale('ru')
  //   messages_to_use = require(`../lang/ru.json`)
  // } else {
  //   messages_to_use = require(`../lang/en.json`)
  // }

  const [selectedTheme, setSelectedTheme] = useState({
    ...theme,
    colors: { ...COLOR_THEME_OPTIONS[LIGHT_THEME] },
    theme_name: LIGHT_THEME,
    theme_color: LIGHT_THEME,
    isRTL: false,
    whitelabel: null,
  })
  const [changeThemeTo, setChangeThemeTo] = useState(null)
  const [changeTestTheme, setChangeTestTheme] = useState(false)
  const [changeDirectionTo, setChangeDirectionTo] = useState(null)

  const [isGettingWhiteLabel, setIsGettingWhiteLabel] = useState(true)

  const [messages, setMessages] = useState(require(`../lang/en.json`))
  const [locale, setLocale] = useState(LANGUAGE_ENGLISH)

  const changeLanguageTo = ({ language }) => {
    let require_path_language = language

    if (require_path_language === 'en') {
      require_path_language = 'en-gb'
    }

    require(`moment/locale/${require_path_language}`)
    moment.locale(language)
    setMessages(require(`../lang/${language}.json`))
    setLocale(language)
  }

  useEffect(() => {
    const { settings } = user || {}

    let { color_theme: user_theme_name, direction, language } = settings || {}

    const current_language = localStorage.getItem(LOCAL_STORAGE_LANGUAGE)

    if (current_language) {
      language = current_language
    }

    if (language) {
      if (language !== LANGUAGE_ENGLISH) {
        changeLanguageTo({ language: 'ru' })
      }
    } else {
      console.log('no language in settings')
      if (typeof navigator !== 'undefined' && navigator && navigator.language) {
        const [language] = navigator.language.split(',')

        if (language) {
          const locale = language.split('-')[0]

          if (locale.length !== 2) {
            console.error('locale is not 2 characters', locale, navigator.language)
          }

          if (locale !== LANGUAGE_ENGLISH && SUPPORTED_LANGUAGES.includes(locale)) {
            changeLanguageTo({ language: locale })
          }
        }
      }
    }

    if (!user_theme_name) {
      const localStorageTheme = localStorage.getItem(LOCAL_STORAGE_KEY_THEME)

      if (localStorageTheme) {
        user_theme_name = localStorageTheme
      }
    }

    if (!direction) {
      const localStorageDirection = localStorage.getItem(LOCAL_STORAGE_KEY_DIRECTION)

      if (localStorageDirection) {
        direction = localStorageDirection
      }
    }

    let user_theme_color = user_theme_name

    if (user_theme_name === SYSTEM_THEME && process.browser) {
      const system_match = window.matchMedia('(prefers-color-scheme: dark)')

      user_theme_name = SYSTEM_THEME
      user_theme_color = system_match.matches ? DARK_THEME : LIGHT_THEME
    }

    let theme_needs_update = false

    if (
      user_theme_color &&
      COLOR_THEME_OPTIONS[user_theme_color] &&
      !selectedTheme.must_not_auto_change &&
      (selectedTheme.theme_name !== user_theme_name || selectedTheme.theme_color !== user_theme_color)
    ) {
      selectedTheme.colors = { ...COLOR_THEME_OPTIONS[user_theme_color] }

      selectedTheme.theme_name = user_theme_name
      selectedTheme.theme_color = user_theme_color

      localStorage.setItem(LOCAL_STORAGE_KEY_THEME, user_theme_name)
    }

    if (
      selectedTheme.theme_color &&
      selectedTheme?.whitelabel?.theme &&
      selectedTheme.whitelabel.theme[selectedTheme.theme_color] &&
      selectedTheme.whitelabel.theme[selectedTheme.theme_color].colors
    ) {
      const new_theme_colors = {
        ...selectedTheme.colors,
        ...selectedTheme.whitelabel.theme[selectedTheme.theme_color].colors,
      }

      if (JSON.stringify(selectedTheme.colors) !== JSON.stringify(new_theme_colors)) {
        selectedTheme.colors = new_theme_colors

        theme_needs_update = true
      }
    }

    if (direction && direction === DIRECTION_RIGHT_TO_LEFT && !selectedTheme.isRTL) {
      theme_needs_update = true
      selectedTheme.isRTL = true
    }

    if (theme_needs_update) {
      setSelectedTheme({ ...selectedTheme })
    }
  }, [selectedTheme])

  const changeTheme = () => {
    const { theme_color, theme_name, check_if_system_theme_allowed } = changeThemeTo

    let allowed_to_change = true

    if (check_if_system_theme_allowed) {
      if (selectedTheme.theme_name !== SYSTEM_THEME) {
        allowed_to_change = false
      }
    }

    if (allowed_to_change) {
      localStorage.setItem(LOCAL_STORAGE_KEY_THEME, theme_name)

      if (user) {
        if (!user.settings) {
          user.settings = {}
        }

        user.settings.color_theme = theme_name
      }

      delete selectedTheme.must_not_auto_change

      selectedTheme.auto_force_update = new Date() // not sure if this is user

      setSelectedTheme({ ...selectedTheme })

      window.dispatchEvent(new CustomEvent('handleChangeThemeInWithConfirm', { detail: { theme_color, theme_name } }))
    }
  }

  const changeDirection = () => {
    const { direction } = changeDirectionTo

    localStorage.setItem(LOCAL_STORAGE_KEY_DIRECTION, direction)

    if (user) {
      if (!user.settings) {
        user.settings = {}
      }

      user.settings.direction = direction
    }

    selectedTheme.isRTL = direction === DIRECTION_RIGHT_TO_LEFT

    setSelectedTheme({ ...selectedTheme })

    window.dispatchEvent(new CustomEvent('handleChangeDirectionInWithConfirm', { detail: { direction } }))
  }

  useEffect(() => {
    if (changeThemeTo) {
      changeTheme()

      setChangeThemeTo(null)
    }
  }, [changeThemeTo])

  useEffect(() => {
    if (changeDirectionTo) {
      changeDirection()

      setChangeDirectionTo(null)
    }
  }, [changeDirectionTo])

  useEffect(() => {
    if (changeTestTheme) {
      const objectKeysColorTheme = Object.keys(COLOR_THEME_OPTIONS)

      const colorThemeIndex = objectKeysColorTheme.findIndex((key) => key === selectedTheme.theme_color)

      let theme_color
      let theme_name

      if (objectKeysColorTheme[colorThemeIndex + 1]) {
        theme_color = COLOR_THEME_OPTIONS[objectKeysColorTheme[colorThemeIndex + 1]]
        theme_name = objectKeysColorTheme[colorThemeIndex + 1]
      } else {
        theme_color = COLOR_THEME_OPTIONS[objectKeysColorTheme[0]]
        // eslint-disable-next-line prefer-destructuring
        theme_name = objectKeysColorTheme[0]
      }

      selectedTheme.must_not_auto_change = true

      selectedTheme.colors = { ...theme_color }

      if (
        selectedTheme?.whitelabel?.theme &&
        selectedTheme.whitelabel.theme[theme_name] &&
        selectedTheme.whitelabel.theme[theme_name].colors
      ) {
        selectedTheme.colors = {
          ...selectedTheme.colors,
          ...selectedTheme.whitelabel.theme[theme_name].colors,
        }
      }

      selectedTheme.theme_name = theme_name
      selectedTheme.theme_color = theme_name

      setSelectedTheme({ ...selectedTheme })

      window.dispatchEvent(
        new CustomEvent('handleChangeThemeInWithConfirm', { detail: { theme_color: theme_name, theme_name } })
      )

      setChangeTestTheme(false)
    }
  }, [changeTestTheme])

  const handleClickChangeTheme = (event) => {
    const { detail = {} } = event
    const { theme_color, theme_name } = detail || {}

    setChangeThemeTo({ ...{ theme_color, theme_name } })
  }

  const handleClickChangeDirection = (event) => {
    const { detail = {} } = event
    const { direction } = detail || {}

    setChangeDirectionTo({ ...{ direction } })
  }

  const handleClickChangeLanguage = (event) => {
    const { detail = {} } = event
    const { language } = detail || {}

    localStorage.setItem(LOCAL_STORAGE_LANGUAGE, language)

    if (user) {
      if (!user.settings) {
        user.settings = {}
      }

      user.settings.language = language
    }

    changeLanguageTo({ language })
  }

  const handleChangeThemeForTests = (event) => {
    if (process.env.NODE_ENV === 'development') {
      if (event.altKey && event.shiftKey && event.keyCode === 69) {
        setChangeTestTheme(true)
      }
    }
  }

  const handleChangeThemeFromOperationSystem = (event) => {
    setChangeThemeTo({
      ...{
        theme_name: SYSTEM_THEME,
        theme_color: event.matches ? DARK_THEME : LIGHT_THEME,
        check_if_system_theme_allowed: true,
      },
    })
  }

  const applyWhiteLabelTheme = ({ whitelabel }) => {
    if (whitelabel) {
      selectedTheme.whitelabel = whitelabel

      setSelectedTheme({ ...selectedTheme })

      window.dispatchEvent(new CustomEvent('handleChangeWhiteLabelInWithConfirm', { detail: { whitelabel } }))
    }
  }

  const handleApplyWhiteLabelTheme = (event) => {
    const { detail = {} } = event
    const { whitelabel } = detail || {}

    applyWhiteLabelTheme({ whitelabel })
  }

  const getWhiteLabel = async () => {
    try {
      const response = await fetch(`/api/white_label/find`)

      if (response && response.status === 200) {
        const json = await response.json()

        const { error, data } = json

        if (!error) {
          data.created = Date.now()

          localStorage.setItem('whitelabel', JSON.stringify(data))

          applyWhiteLabelTheme({ whitelabel: data })
        }
      }
    } catch (error) {
      errorHelper({ error, componentName: App.displayName, functionName: 'getWhiteLabel', showAlert: false })
    } finally {
      setIsGettingWhiteLabel(false)
    }
  }

  const checkWhiteLabel = () => {
    if (`https://${document.location.hostname}` !== process.env.NEXT_PUBLIC_SITE_URL) {
      const whitelabel = localStorage.getItem('whitelabel')

      if (!whitelabel) {
        getWhiteLabel()
      } else {
        const parsed = JSON.parse(whitelabel)

        if (!parsed.created || parsed.created < Date.now() - 1000 * 60 * 5) {
          applyWhiteLabelTheme({ whitelabel: parsed })

          getWhiteLabel()
        } else {
          applyWhiteLabelTheme({ whitelabel: parsed })

          setIsGettingWhiteLabel(false)
        }
      }
    } else {
      setIsGettingWhiteLabel(false)
    }
  }

  useEffect(() => {
    checkWhiteLabel()

    window.addEventListener('handleUpdateUserTheme', handleClickChangeTheme)
    window.addEventListener('handleUpdateUserDirection', handleClickChangeDirection)
    window.addEventListener('handleUpdateUserLanguage', handleClickChangeLanguage)
    window.addEventListener('handleApplyWhiteLabelTheme', handleApplyWhiteLabelTheme)
    window.addEventListener('keydown', handleChangeThemeForTests)

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', handleChangeThemeFromOperationSystem)

    return () => {
      window.removeEventListener('handleUpdateUserTheme', handleClickChangeTheme)
      window.removeEventListener('handleUpdateUserDirection', handleClickChangeDirection)
      window.removeEventListener('handleUpdateUserLanguage', handleClickChangeLanguage)
      window.removeEventListener('handleApplyWhiteLabelTheme', handleApplyWhiteLabelTheme)
      window.removeEventListener('keydown', handleChangeThemeForTests)

      window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', handleChangeThemeFromOperationSystem)
    }
  }, [])

  return (
    <ErrorBoundary>
      <IntlProvider locale={locale} defaultLocale={LANGUAGE_ENGLISH} messages={messages}>
        {title && path && (
          <Head>
            <title>{messages[title]} | Vista Social</title>
            <link rel="canonical" href={path} />
            <link rel="shortcut icon" type="image/x-icon" href={'/assets/favicon.ico?v=1'} />
            <meta name="viewport" content="width=device-width, initial-scale=1.0,maximum-scale=1.0" />
          </Head>
        )}

        <ThemeProvider theme={selectedTheme}>
          <React.Fragment>
            <NextNprogress
              color={selectedTheme.colors.primary}
              startPosition={0.3}
              stopDelayMs={200}
              options={{ easing: 'ease', speed: 500, showSpinner: false }}
              height={3}
            />
            <GlobalStyles theme={selectedTheme} />

            {isGettingWhiteLabel ? (
              <Flex
                alignItems="center"
                justifyContent="center"
                height="100%"
                width="100%"
                className="cliploader-wrapper"
              >
                <ClipLoader size="50" />
              </Flex>
            ) : (
              <Fragment>{children}</Fragment>
            )}

            <Alert stack={{ limit: 3 }} contentTemplate={AlertTemplate} position="top-right" />
          </React.Fragment>
        </ThemeProvider>
      </IntlProvider>
    </ErrorBoundary>
  )
}

App.defaultProps = {
  user: null,
}

App.propTypes = {
  children: PropTypes.node.isRequired,
  user: PropTypes.object,
}

App.displayName = 'App'

export default withoutPageDataProps(App)
